import { generatePath } from 'react-router-dom';

import { ORDER_ROUTES } from '../../../order/order.constants';
import { PAYMENT_ROUTES } from '../../payment.constants';
import { ORDER_STATE, OrderState, resolveOrderState } from './order-state';

/**
 * What WaitScreen does with one `/api/order` response.
 *
 * One resolved state (see `order-state.ts`) plus one table per flow. Adding a flow
 * means adding a table; adding a state means the compiler lists every table that
 * has to answer for it. See `.claude/rules/branching-logic.md` Rules 4 and 5.
 *
 * Two things the tables deliberately keep separate, because the old inline code
 * proved they are independent:
 *  - **where to go** (`to`) and **whether the order is finished** (`finishes`).
 *    CT5 on a refunded-session-closed order navigates without finishing; CT10 on a
 *    dispensed order finishes without navigating, leaving the next timer tick to
 *    move the screen.
 *  - the per-state decision and the **last-poll grace rules** below, which can
 *    override a table's `to` on the final tick only.
 */

/** Where WaitScreen sends the customer; `STAY` keeps the screen and polls again. */
const DESTINATION = {
    STAY: 'stay',
    SUCCESS: 'success',
    FAILED: 'failed',
    REFUND: 'refund',
    SELECT: 'select',
} as const;

type Destination = (typeof DESTINATION)[keyof typeof DESTINATION];

/**
 * Debug lines for the flow tables, named once. `SILENT` logs nothing; a `null`
 * `log` in a table falls back to printing the raw pair.
 */
const FLOW_LOG = {
    SILENT: '',
    KMG_87_ORDER_COMPLETED: 'kmg - 87 - order completed',
    KMG_89_ORDER_REFUNDED: 'kmg - 89 - order refunded',
    KMG_91_ORDER_CANCELLED: 'kmg - 91 - order cancelled',
    KMG_95_CT10_FLOW_COMPLETED: 'kmg - 95 - CT10 flow completed',
    REFUND_STATUS_4_SESSION_CLOSED: 'refund - status 4, session closed',
    REFUND_STATUS_6_MANUAL_RETURN: 'refund - status 6, manual return',
    SWITCH_CT5_CONNECTED: 'switch - CT5 connected',
    SWITCH_CT5_MANUAL_REFUND: 'switch - CT5 manual refund',
    SWITCH_CT5_PAID: 'switch - CT5 paid',
    SWITCH_CT5_REFUNDED: 'switch - CT5 refunded',
    SWITCH_CT5_SELECTED: 'switch - CT5 selected',
    SWITCH_CT5_SESSION_CLOSED_SALE_PENDING:
        'switch - CT5 session closed, sale pending',
    SWITCH_FLOW_COMPLETED: 'switch - flow completed',
    SWITCH_PAYMENT_FAILED: 'switch - payment failed',
} as const;

type FlowLog = (typeof FLOW_LOG)[keyof typeof FLOW_LOG];

interface FlowStep {
    to: Destination;
    finishes: boolean;
    /**
     * Log line for this state, always one of `FLOW_LOG`. `null` means "use the
     * generic fallback", which prints the raw pair; `FLOW_LOG.SILENT` logs nothing
     * at all. The CT5 flow really does stay silent on states it has no case for —
     * preserved from the original.
     */
    log: FlowLog | null;
    /**
     * On the final poll, leave for the success screen instead of `to`. The state is
     * a sale the platform has not confirmed to this screen yet.
     */
    succeedsOnLastPoll?: boolean;
}

type FlowConfig = Record<OrderState, FlowStep>;

/** The common "keep polling" rows: `WAIT` prints the raw pair, `WAIT_SILENT` nothing. */
const WAIT: FlowStep = { to: DESTINATION.STAY, finishes: false, log: null };
const WAIT_SILENT: FlowStep = {
    to: DESTINATION.STAY,
    finishes: false,
    log: FLOW_LOG.SILENT,
};

/**
 * CT1 — the default flow. A dispensed or closed order does not navigate here: it
 * is marked finished and the next timer tick sends the customer to the success
 * screen.
 */
const CT1_FLOW: FlowConfig = {
    [ORDER_STATE.PAID_AWAITING_DEVICE]: WAIT,
    [ORDER_STATE.PAID_PRODUCT_SELECTED]: WAIT,
    [ORDER_STATE.PAID_CONNECTED]: WAIT,
    [ORDER_STATE.PAID_DEVICE_AHEAD]: WAIT,
    [ORDER_STATE.PAYMENT_FAILED]: {
        to: DESTINATION.FAILED,
        finishes: true,
        log: FLOW_LOG.KMG_91_ORDER_CANCELLED,
    },
    [ORDER_STATE.CHARGED_DISPENSED]: {
        to: DESTINATION.STAY,
        finishes: true,
        log: FLOW_LOG.KMG_87_ORDER_COMPLETED,
    },
    [ORDER_STATE.CHARGED_SESSION_CLOSED]: {
        to: DESTINATION.STAY,
        finishes: true,
        log: FLOW_LOG.KMG_87_ORDER_COMPLETED,
    },
    [ORDER_STATE.CHARGED_PRODUCT_SELECTED]: WAIT,
    [ORDER_STATE.CHARGED_CONNECTED]: WAIT,
    [ORDER_STATE.CHARGED_DEVICE_SILENT]: WAIT,
    [ORDER_STATE.REFUNDED_DISPENSED]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.KMG_89_ORDER_REFUNDED,
    },
    /**
     * SWRN-1900 / plan §5.15. This row used to be `STAY, finishes: false`, so a
     * refunded CT1 order waited out the timer and landed on the *failure* screen
     * — 3,017 orders in 90 days, about 33 a day.
     *
     * `status = 4` alone means the money was returned; `device_status` only
     * records the last Cashless command the machine sent, and `4` is sticky, so
     * `(4,4)` versus `(4,6)` records arrival order and nothing about money. The
     * neighbouring `refunded-dispensed` row is the same refund with the other
     * arrival order, and it has always gone here.
     *
     * `finishes: true` matches that neighbour rather than CT5's version of this
     * row: it makes `shouldPersistOrder` true, so the order is written to the
     * store before we navigate. CT5 navigates without persisting, which the file
     * header already flags as odd — not a pattern to copy.
     */
    [ORDER_STATE.REFUNDED_SESSION_CLOSED]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.REFUND_STATUS_4_SESSION_CLOSED,
    },
    [ORDER_STATE.REFUNDED_DEVICE_OTHER]: WAIT,
    [ORDER_STATE.REFUNDED_MANUALLY]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.REFUND_STATUS_6_MANUAL_RETURN,
    },
    [ORDER_STATE.UNMAPPED]: WAIT,
};

/**
 * CT10 (fridge) — differs from CT1 in exactly one way: any charged order the
 * machine has not explicitly closed is already a success, so the three "charged
 * but the device is behind" states resolve immediately instead of waiting.
 */
const CT10_FLOW: FlowConfig = {
    ...CT1_FLOW,
    [ORDER_STATE.CHARGED_PRODUCT_SELECTED]: {
        to: DESTINATION.SUCCESS,
        finishes: true,
        log: FLOW_LOG.KMG_95_CT10_FLOW_COMPLETED,
    },
    [ORDER_STATE.CHARGED_CONNECTED]: {
        to: DESTINATION.SUCCESS,
        finishes: true,
        log: FLOW_LOG.KMG_95_CT10_FLOW_COMPLETED,
    },
    [ORDER_STATE.CHARGED_DEVICE_SILENT]: {
        to: DESTINATION.SUCCESS,
        finishes: true,
        log: FLOW_LOG.KMG_95_CT10_FLOW_COMPLETED,
    },
};

/**
 * CT5 — the machine is paid first and reports back, so the same tuples mean
 * different things. Note `charged-session-closed`: CT1 reads it as a completed
 * vend, CT5 as a refund. That disagreement is real and predates this refactor; it
 * used to hide inside a predicate called `hasWrongCompletedCombination`, which
 * named an illegal combination and folded it into "refunded" rather than admitting
 * it existed. It is an open question (§5.2), not something to quietly normalise.
 *
 * `finishes` still comes from the generic terminal set — the CT5 flow changed where
 * the customer goes, never whether the order counted as finished.
 */
const CT5_FLOW: FlowConfig = {
    [ORDER_STATE.PAID_AWAITING_DEVICE]: {
        to: DESTINATION.STAY,
        finishes: false,
        log: FLOW_LOG.SWITCH_CT5_PAID,
    },
    [ORDER_STATE.PAID_PRODUCT_SELECTED]: {
        to: DESTINATION.STAY,
        finishes: false,
        log: FLOW_LOG.SWITCH_CT5_SELECTED,
    },
    [ORDER_STATE.PAID_CONNECTED]: {
        to: DESTINATION.SELECT,
        finishes: false,
        log: FLOW_LOG.SWITCH_CT5_CONNECTED,
    },
    [ORDER_STATE.PAID_DEVICE_AHEAD]: WAIT_SILENT,
    [ORDER_STATE.PAYMENT_FAILED]: {
        to: DESTINATION.FAILED,
        finishes: true,
        log: FLOW_LOG.SWITCH_PAYMENT_FAILED,
    },
    [ORDER_STATE.CHARGED_DISPENSED]: {
        to: DESTINATION.SUCCESS,
        finishes: true,
        log: FLOW_LOG.SWITCH_FLOW_COMPLETED,
    },
    [ORDER_STATE.CHARGED_SESSION_CLOSED]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.SWITCH_CT5_REFUNDED,
    },
    [ORDER_STATE.CHARGED_PRODUCT_SELECTED]: WAIT_SILENT,
    [ORDER_STATE.CHARGED_CONNECTED]: WAIT_SILENT,
    [ORDER_STATE.CHARGED_DEVICE_SILENT]: WAIT_SILENT,
    [ORDER_STATE.REFUNDED_DISPENSED]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.SWITCH_CT5_REFUNDED,
    },
    [ORDER_STATE.REFUNDED_SESSION_CLOSED]: {
        to: DESTINATION.REFUND,
        finishes: false,
        log: FLOW_LOG.SWITCH_CT5_REFUNDED,
    },
    [ORDER_STATE.REFUNDED_DEVICE_OTHER]: WAIT_SILENT,
    [ORDER_STATE.REFUNDED_MANUALLY]: {
        to: DESTINATION.REFUND,
        finishes: true,
        log: FLOW_LOG.SWITCH_CT5_MANUAL_REFUND,
    },
    [ORDER_STATE.UNMAPPED]: WAIT_SILENT,
};

/**
 * CT5 pulse (water, snack-pulse, pulse, car wash, popcorn) — SWRN-1893.
 *
 * A pulse machine meters its own dispensing, so there is nothing to pick: it stays
 * on the wait screen until the order is charged, instead of being sent to the
 * product grid. This is the whole behavioural difference, and expressing it as a
 * one-row flow config is the point — it used to be a boolean threaded into the step
 * function and an `if` inside a switch case.
 */
const CT5_PULSE_FLOW: FlowConfig = {
    ...CT5_FLOW,
    [ORDER_STATE.PAID_CONNECTED]: {
        to: DESTINATION.STAY,
        finishes: false,
        log: FLOW_LOG.SWITCH_CT5_CONNECTED,
    },
};

/**
 * CT5 on firmware 6.x with Tranzzo — SWRN-2286.
 *
 * There "charged, session closed" is a sale, not a refund about to happen:
 * payment-service sells the order on the dispense result, and web0 does not refund
 * a sold order when the session closes. The order may get there before web0 has
 * seen the result, so the screen keeps polling instead of leaving for the refund
 * screen, and on the final poll it counts as a completed vend; a real refund still
 * arrives as status 4 and is routed as usual.
 */
const withSessionClosedAsSale = (flow: FlowConfig): FlowConfig => ({
    ...flow,
    [ORDER_STATE.CHARGED_SESSION_CLOSED]: {
        ...WAIT,
        log: FLOW_LOG.SWITCH_CT5_SESSION_CLOSED_SALE_PENDING,
        succeedsOnLastPoll: true,
    },
});

const CT5_FW6_TRANZZO_FLOW = withSessionClosedAsSale(CT5_FLOW);
const CT5_PULSE_FW6_TRANZZO_FLOW = withSessionClosedAsSale(CT5_PULSE_FLOW);

export interface WaitStepFlow {
    isCT5: boolean;
    isCT10: boolean;
    isPulse: boolean;
    /**
     * On the final poll only, a charged order still reporting "product selected"
     * counts as a completed vend for this machine.
     */
    treatsProductSelectedAsSuccess: boolean;
    /** Same grace rule, for a machine that reports "connected" instead. */
    treatsConnectedAsSuccess: boolean;
    /**
     * A charged order whose machine closed the session is a sale, not a refund:
     * selects the CT5 table that waits on it. CT5 on firmware 6.x with Tranzzo only.
     */
    treatsSessionClosedAsSale: boolean;
}

export interface WaitStepInput {
    status: number;
    deviceStatus: number | null;
    orderId: string | null;
    /** Seconds left on the wait timer. The grace rules fire on the last one only. */
    timerTime: number;
    flow: WaitStepFlow;
}

export interface WaitStep {
    logMessage: string;
    /** Empty string means "stay on this screen" — that is real behaviour. */
    navigatePath: string;
    /** The order reached a terminal state; the next timer tick navigates. */
    isFinished: boolean;
    /** The order object should be written to the store before navigating. */
    shouldPersistOrder: boolean;
}

/** The tick on which the grace rules apply. */
const LAST_POLL = 1;

const selectCt5Flow = ({
    isPulse,
    treatsSessionClosedAsSale,
}: WaitStepFlow): FlowConfig => {
    if (treatsSessionClosedAsSale) {
        return isPulse ? CT5_PULSE_FW6_TRANZZO_FLOW : CT5_FW6_TRANZZO_FLOW;
    }
    return isPulse ? CT5_PULSE_FLOW : CT5_FLOW;
};

const selectFlow = (flow: WaitStepFlow): FlowConfig => {
    if (flow.isCT5) return selectCt5Flow(flow);
    if (flow.isCT10) return CT10_FLOW;
    return CT1_FLOW;
};

const toPath = (destination: Destination, orderId: string): string => {
    switch (destination) {
        case DESTINATION.SUCCESS:
            return generatePath(ORDER_ROUTES.success, { id: orderId });
        case DESTINATION.FAILED:
            return generatePath(ORDER_ROUTES.failed, { id: orderId });
        case DESTINATION.REFUND:
            return generatePath(ORDER_ROUTES.refund, { id: orderId });
        case DESTINATION.SELECT:
            return generatePath(PAYMENT_ROUTES.select, { id: orderId });
        case DESTINATION.STAY:
            return '';
        default: {
            // Exhaustive: adding a Destination without a path is a compile error.
            const unreachable: never = destination;
            return unreachable;
        }
    }
};

/**
 * Grace rules — the last poll only.
 *
 * By the final tick the order is charged but the machine never advanced past
 * "product selected" or "connected". Rather than time the customer out, these
 * machines treat that as a completed vend. They override whatever the flow table
 * decided, which is why they are applied after it. A table row can ask for the
 * same with `succeedsOnLastPoll`.
 */
const grantsLastPollSuccess = (
    state: OrderState,
    timerTime: number,
    flow: WaitStepFlow,
): boolean => {
    if (timerTime !== LAST_POLL) return false;

    if (
        state === ORDER_STATE.CHARGED_PRODUCT_SELECTED &&
        flow.treatsProductSelectedAsSuccess
    ) {
        return true;
    }

    return (
        state === ORDER_STATE.CHARGED_CONNECTED && flow.treatsConnectedAsSuccess
    );
};

export const resolveWaitStep = ({
    status,
    deviceStatus,
    orderId,
    timerTime,
    flow,
}: WaitStepInput): WaitStep => {
    const state = resolveOrderState(status, deviceStatus);
    const step = selectFlow(flow)[state];
    const navId = String(orderId);

    const isLastPollSuccess =
        grantsLastPollSuccess(state, timerTime, flow) ||
        (timerTime === LAST_POLL && step.succeedsOnLastPoll === true);

    return {
        logMessage: step.log ?? `s: ${status} ds: ${deviceStatus}`,
        navigatePath: isLastPollSuccess
            ? toPath(DESTINATION.SUCCESS, navId)
            : toPath(step.to, navId),
        isFinished: step.finishes,
        // The grace rules wrote the order to the store before navigating, and so
        // did the terminal check. One flag covers both.
        shouldPersistOrder: isLastPollSuccess || step.finishes,
    };
};
